import { z } from 'zod';

// Device type enum.
export const DeviceTypeEnum = z.enum(['WISE-4060', 'WISE-4060LAN']);
export type DeviceTypeEnum = z.infer<typeof DeviceTypeEnum>;

// Device status enum.
export const DeviceStatusEnum = z.enum(['ONLINE', 'OFFLINE', 'ERROR']);
export type DeviceStatusEnum = z.infer<typeof DeviceStatusEnum>;

// Relay state enum.
export const RelayStateEnum = z.enum(['OPEN', 'CLOSED', 'UNKNOWN']);
export type RelayStateEnum = z.infer<typeof RelayStateEnum>;

// Device role enum.
export const DeviceRoleEnum = z.enum(['ENTRY', 'EXIT', 'BIDIRECTIONAL']);
export type DeviceRoleEnum = z.infer<typeof DeviceRoleEnum>;

// Command action enum.
export const CommandActionEnum = z.enum(['open', 'close']);
export type CommandActionEnum = z.infer<typeof CommandActionEnum>;

// Device schema.
export const Device = z.object({
  id: z.string(),
  name: z.string(),
  device_type: DeviceTypeEnum,
  mac_address: z.string(),
  location_id: z.string(),
  location_name: z.string().optional(),
  relay_channel: z.number(),
  status: DeviceStatusEnum,
  relay_state: RelayStateEnum,
  last_seen: z.string(),
  ip_address: z.string().nullable().optional(),
  role: DeviceRoleEnum.nullable().optional(),
  camera_id: z.string().nullable().optional(),
  camera_name: z.string().nullable().optional(),
  iot_thing_name: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});

export type DeviceType = z.infer<typeof Device>;
export const DeviceList = z.array(Device);
export type DeviceListType = z.infer<typeof DeviceList>;

// Device command request.
export const DeviceCommand = z.object({
  action: CommandActionEnum,
  hold_seconds: z.number().min(0).max(300).optional(),
  reason: z.string().max(500).optional(),
});

export type DeviceCommandType = z.infer<typeof DeviceCommand>;

// Device command result.
export const DeviceCommandResult = z.object({
  success: z.boolean(),
  device_id: z.string(),
  action: z.string(),
  executed_at: z.string(),
  error_message: z.string().nullable().optional(),
  request_id: z.string(),
});

export type DeviceCommandResultType = z.infer<typeof DeviceCommandResult>;

// Create device request.
export const CreateDeviceRequest = z.object({
  name: z.string().min(1).max(100),
  device_type: DeviceTypeEnum,
  mac_address: z.string(),
  location_id: z.string(),
  relay_channel: z.number().min(0).max(3),
  role: DeviceRoleEnum.optional(),
  camera_id: z.string().optional(),
  ip_address: z.string().optional(),
});

export type CreateDeviceRequestType = z.infer<typeof CreateDeviceRequest>;

// Update device request.
export const UpdateDeviceRequest = z.object({
  name: z.string().min(1).max(100).optional(),
  role: DeviceRoleEnum.nullable().optional(),
  camera_id: z.string().nullable().optional(),
  ip_address: z.string().nullable().optional(),
  status: DeviceStatusEnum.optional(),
  relay_state: RelayStateEnum.optional(),
});

export type UpdateDeviceRequestType = z.infer<typeof UpdateDeviceRequest>;

// Access decision enums.
export const AccessDecisionResultEnum = z.enum(['GRANTED', 'DENIED']);
export type AccessDecisionResultEnum = z.infer<typeof AccessDecisionResultEnum>;

// Mirrors AccessDecisionSource on the server
// (internal/infra/db/ent/schema/access_decision.go). Keep in sync — when the
// server adds a value, this enum must accept it or the entire decisions
// response is rejected by Zod and the Activity tab renders nothing.
export const AccessDecisionSourceEnum = z.enum([
  'API',
  'LOCAL_WHITELIST',
  'MANUAL_PORTAL',
  'PEDESTRIAN_QR',
  'MANUAL_KEY',
  'PIN',
  'OTP',
  'SUBSCRIBER_APP',
]);
export type AccessDecisionSourceEnum = z.infer<typeof AccessDecisionSourceEnum>;

export const GateActionResultEnum = z.enum(['OPEN', 'CLOSE', 'NONE']);
export type GateActionResultEnum = z.infer<typeof GateActionResultEnum>;

export const ApiFailureReasonEnum = z.enum(['TIMEOUT', 'UNAVAILABLE', 'ERROR']);
export type ApiFailureReasonEnum = z.infer<typeof ApiFailureReasonEnum>;

export const AccessRecordTypeEnum = z.enum([
  'SESSION',
  'PERMIT',
  'SUBSCRIPTION',
]);
export type AccessRecordTypeEnum = z.infer<typeof AccessRecordTypeEnum>;

// Access decision schema.
export const AccessDecision = z.object({
  id: z.string(),
  device_id: z.string(),
  device_name: z.string().optional().default(''),
  plate: z.string(),
  decision: AccessDecisionResultEnum,
  reason: z.string(),
  record_type: AccessRecordTypeEnum.nullable().optional(),
  record_id: z.string().nullable().optional(),
  // .catch keeps the Activity tab rendering if the server adds a new source value
  // before this enum is updated (defends the documented keep-in-sync contract above);
  // unknown values fall back to MANUAL_PORTAL until the enum + chip are updated.
  source: AccessDecisionSourceEnum.catch('MANUAL_PORTAL'),
  api_failure_reason: z.string().nullable().optional(),
  gate_action: GateActionResultEnum.catch('NONE'),
  response_time_ms: z.number().nullable().optional(),
  timestamp: z.string(),
  camera_id: z.string().nullable().optional(),
  camera_name: z.string().nullable().optional(),
  initiated_by: z.string().nullable().optional(),
  // True when the decision has viewable camera evidence (a linked camera event or a captured
  // snapshot); drives the evidence affordance on the row. Older responses omit it -> no affordance.
  has_evidence: z.boolean().optional().default(false),
});

export type AccessDecisionType = z.infer<typeof AccessDecision>;
export const AccessDecisionList = z.array(AccessDecision);
export type AccessDecisionListType = z.infer<typeof AccessDecisionList>;

// Access decisions response (paginated).
export const AccessDecisionsResponse = z.object({
  decisions: AccessDecisionList,
  total: z.number(),
});
export type AccessDecisionsResponseType = z.infer<
  typeof AccessDecisionsResponse
>;

// Whitelist entry source enum (reuses AccessRecordTypeEnum but adds MANUAL).
export const WhitelistEntrySourceEnum = z.enum([
  'SESSION',
  'PERMIT',
  'SUBSCRIPTION',
  'MANUAL',
]);
export type WhitelistEntrySourceEnum = z.infer<typeof WhitelistEntrySourceEnum>;

// Whitelist entry schema.
export const WhitelistEntry = z.object({
  id: z.string(),
  plate: z.string(),
  source: WhitelistEntrySourceEnum,
  source_id: z.string().nullable().optional(),
  valid_from: z.string().nullable(),
  valid_until: z.string().nullable(),
  added_at: z.string(),
  priority: z.number(),
  holder_name: z.string().nullable().optional(),
});

export type WhitelistEntryType = z.infer<typeof WhitelistEntry>;
export const WhitelistEntryList = z.array(WhitelistEntry);
export type WhitelistEntryListType = z.infer<typeof WhitelistEntryList>;

// Whitelist entries response.
export const WhitelistEntriesResponse = z.object({
  entries: WhitelistEntryList,
  total: z.number(),
});
export type WhitelistEntriesResponseType = z.infer<
  typeof WhitelistEntriesResponse
>;

// Whitelist sync state enum.
export const WhitelistSyncStateEnum = z.enum([
  'SYNCED',
  'SYNCING',
  'FAILED',
  'STALE',
]);
export type WhitelistSyncStateEnum = z.infer<typeof WhitelistSyncStateEnum>;

// Whitelist status schema.
export const WhitelistStatus = z.object({
  device_id: z.string(),
  last_sync: z.string(),
  last_successful_sync: z.string().nullable().optional(),
  entry_count: z.number(),
  sync_status: WhitelistSyncStateEnum,
  next_sync: z.string().nullable().optional(),
  error_message: z.string().nullable().optional(),
});

export type WhitelistStatusType = z.infer<typeof WhitelistStatus>;

// Camera capture image URLs from presigned S3 URLs.
export const CaptureImages = z.object({
  full: z.string().optional(),
  cropped: z.string().optional(),
  vehicle: z.string().optional(),
});
export type CaptureImagesType = z.infer<typeof CaptureImages>;

// A still actively captured at a pedestrian-door unlock (presigned S3 URL).
export const SnapshotImage = z.object({
  camera_id: z.string().nullable().optional(),
  url: z.string(),
  captured_at: z.number(),
});
export type SnapshotImageType = z.infer<typeof SnapshotImage>;

// Camera capture response for access decision correlation.
export const CameraCaptureResponse = z.object({
  access_decision_id: z.string(),
  camera_event_id: z.string().nullable().optional(),
  camera_id: z.string().nullable().optional(),
  plate: z.string().optional(),
  captured_at: z.number().nullable().optional(),
  images: CaptureImages.nullable().optional(),
  active_snapshots: z.array(SnapshotImage).optional(),
  correlation_method: z.string(),
  message: z.string().optional(),
});
export type CameraCaptureResponseType = z.infer<typeof CameraCaptureResponse>;

// ============================================================================
// Access Control Schedule Types
// ============================================================================

// Access policy enum.
export const AccessPolicyEnum = z.enum([
  'FREE_FLOW',
  'OPEN_ENTRY',
  'WHITELIST_ONLY',
  'PERMIT_HOLDERS',
]);
export type AccessPolicyEnum = z.infer<typeof AccessPolicyEnum>;

// Gate state enum.
export const GateStateEnum = z.enum([
  'HELD_OPEN',
  'CLOSED',
  'SECURED',
  'LOCKED',
]);
export type GateStateEnum = z.infer<typeof GateStateEnum>;

// Day of week enum.
export const DayOfWeekEnum = z.enum([
  'MON',
  'TUE',
  'WED',
  'THU',
  'FRI',
  'SAT',
  'SUN',
]);
export type DayOfWeekEnum = z.infer<typeof DayOfWeekEnum>;

// Schedule period schema.
export const AccessControlPeriod = z.object({
  id: z.string(),
  days_of_week: z.array(DayOfWeekEnum),
  start_time: z.string(), // "HH:mm"
  end_time: z.string(), // "HH:mm"
  access_policy: AccessPolicyEnum,
});
export type AccessControlPeriodType = z.infer<typeof AccessControlPeriod>;

// Override period schema.
export const AccessControlOverridePeriod = z.object({
  id: z.string(),
  start_time: z.string(),
  end_time: z.string(),
  access_policy: AccessPolicyEnum,
});
export type AccessControlOverridePeriodType = z.infer<
  typeof AccessControlOverridePeriod
>;

// Schedule override schema.
export const AccessControlOverride = z.object({
  id: z.string(),
  name: z.string(),
  start_date: z.string(), // "YYYY-MM-DD"
  end_date: z.string(),
  is_closed_all_day: z.boolean(),
  periods: z.array(AccessControlOverridePeriod),
});
export type AccessControlOverrideType = z.infer<typeof AccessControlOverride>;

// Full schedule schema.
export const AccessControlSchedule = z.object({
  id: z.string(),
  location_id: z.string(),
  name: z.string(),
  default_access_policy: AccessPolicyEnum,
  is_active: z.boolean(),
  periods: z.array(AccessControlPeriod),
  overrides: z.array(AccessControlOverride),
  created_at: z.string(),
  updated_at: z.string(),
});
export type AccessControlScheduleType = z.infer<typeof AccessControlSchedule>;

// Schedule template schema.
export const ScheduleTemplate = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  default_access_policy: AccessPolicyEnum,
  periods: z.array(
    z.object({
      days_of_week: z.array(DayOfWeekEnum),
      start_time: z.string(),
      end_time: z.string(),
      access_policy: AccessPolicyEnum,
    })
  ),
});
export type ScheduleTemplateType = z.infer<typeof ScheduleTemplate>;

// Templates response (API returns array directly).
export const ScheduleTemplatesResponse = z.array(ScheduleTemplate);
export type ScheduleTemplatesResponseType = z.infer<
  typeof ScheduleTemplatesResponse
>;

// Create/update schedule request.
export const CreateScheduleRequest = z.object({
  name: z.string().min(1).max(100),
  default_access_policy: AccessPolicyEnum,
  is_active: z.boolean().optional(),
  periods: z
    .array(
      z.object({
        days_of_week: z.array(DayOfWeekEnum).min(1),
        start_time: z.string(),
        end_time: z.string(),
        access_policy: AccessPolicyEnum,
      })
    )
    .optional(),
});
export type CreateScheduleRequestType = z.infer<typeof CreateScheduleRequest>;

// Create period request.
export const CreatePeriodRequest = z.object({
  days_of_week: z.array(DayOfWeekEnum).min(1),
  start_time: z.string(),
  end_time: z.string(),
  access_policy: AccessPolicyEnum,
});
export type CreatePeriodRequestType = z.infer<typeof CreatePeriodRequest>;

// Create override request.
export const CreateOverrideRequest = z.object({
  name: z.string().min(1).max(100),
  start_date: z.string(),
  end_date: z.string(),
  is_closed_all_day: z.boolean().optional(),
  periods: z
    .array(
      z.object({
        start_time: z.string(),
        end_time: z.string(),
        access_policy: AccessPolicyEnum,
      })
    )
    .optional(),
});
export type CreateOverrideRequestType = z.infer<typeof CreateOverrideRequest>;

// Update override request.
export const UpdateOverrideRequest = z.object({
  name: z.string().min(1).max(100).optional(),
  start_date: z.string().optional(),
  end_date: z.string().optional(),
  is_closed_all_day: z.boolean().optional(),
  periods: z
    .array(
      z.object({
        start_time: z.string(),
        end_time: z.string(),
        access_policy: AccessPolicyEnum,
      })
    )
    .optional(),
});
export type UpdateOverrideRequestType = z.infer<typeof UpdateOverrideRequest>;

// Access point schedule override response.
export const AccessPointOverride = z.object({
  id: z.string(),
  access_point_id: z.string(),
  override_location_schedule: z.boolean(),
  custom_schedule_id: z.string().nullable().optional(),
  access_policy_override: AccessPolicyEnum.nullable().optional(),
});
export type AccessPointOverrideType = z.infer<typeof AccessPointOverride>;

// Access point effective schedule response.
export const AccessPointSchedule = z.object({
  access_point_id: z.string(),
  source: z.enum(['LOCATION_DEFAULT', 'ACCESS_POINT_OVERRIDE']),
  effective_schedule: AccessControlSchedule.nullable().optional(),
  effective_access_policy: AccessPolicyEnum,
  override: AccessPointOverride.nullable().optional(),
});
export type AccessPointScheduleType = z.infer<typeof AccessPointSchedule>;

// Set access point schedule request.
export const SetAccessPointScheduleRequest = z.object({
  override_location_schedule: z.boolean(),
  access_policy_override: AccessPolicyEnum.nullable().optional(),
});
export type SetAccessPointScheduleRequestType = z.infer<
  typeof SetAccessPointScheduleRequest
>;
